import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { logEvent, getAnalytics } from 'firebase/analytics';
import MultiStepActivationContainer from './Components/MultiStepActivationContainer';
import { useTranslation, Trans } from 'react-i18next';
import { themeColor } from '../Theming';
import images from '../Theming/images';
import { useNavigate } from 'react-router-dom';
import { Path } from '../Routing';
import app from '../Firebase';
import { useQuery } from '../Common/Hooks';
import {
  ANALYTICS_ACTION_LABELS,
  ANALYTICS_PAGE_NAMES,
  PartnerNames,
  trackActivationActionEvent,
} from '../Common/Analytics/utils';

const SubTitleTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 24px;
`;

const SubTitleText = styled.div`
  color: ${themeColor('darkestGrey')};
  text-align: center;
  font-family: Arial;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  width: 240px;
`;

const LinkText = styled.a`
  color: ${themeColor('blue')};
`;

export const HyperisePlaceholderContainer = styled.div`
  width: 100%;
  height: fit-content;
`;

const BodyContentContainer = styled.div`
  width: 100%;
  margin-top: 33px;
  display: flex;
  justify-content: center;
`;

const ListContent = styled.li`
  color: ${themeColor('darkestGrey')};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 21px;
  margin-left: 15px;
  margin-bottom: 20px;
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-20px);
  }
  60% {
    transform: translateY(-10px);
  }
`;

const StyledImage = styled.img`
  position: fixed;
  top: 8px;
  right: 20px;
  animation: ${bounce} 2s infinite;
  width: 163px;
  height: 46px;
`;

const AlwayAllowImage = styled.img`
  width: auto;
  height: 169px;
`;

const PartnerNewPluginActivateMultiStepAlwaysAllow = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const query = useQuery();
  const { activation_permissions } = ANALYTICS_ACTION_LABELS;
  const { additional_permissions_page } = ANALYTICS_PAGE_NAMES;
  const partner = query.get('utm_campaign') as PartnerNames;

  useEffect(() => {
    const analytics = getAnalytics(app);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const messageHandler = (message: any) => {
      if (message) {
        switch (message.data?.type) {
          case 'PluginEnabled': {
            const origin = query.get('origin');
            trackActivationActionEvent(
              query,
              partner,
              activation_permissions,
              additional_permissions_page,
              {
                permissions: message.data?.givenPermission ? 'Full' : 'Partial',
              }
            );
            logEvent(analytics, 'activation_permissions', {
              permissions: message.data?.givenPermission ? 'Full' : 'Partial',
              partner: origin,
            });

            if (message.data?.givenPermission) {
              navigate({
                pathname: Path.PluginActivationV2StartShopping,
                search: location.search,
              });
            } else {
              navigate({
                pathname: Path.PluginActivationV2PartialPermissions,
                search: location.search,
              });
            }
            break;
          }
        }
      }
    };

    window.addEventListener('message', messageHandler);

    return () => window.removeEventListener('message', messageHandler);
  }, [history]);

  return (
    <MultiStepActivationContainer
      bodyMarginTop={25}
      headerText={'PartnerNewpluginsdkActivate.youAreAlmostDoneHeaderText'}
    >
      <StyledImage src={images.partnerpluginsdk_tap_review_itvx} />
      <SubTitleTextContainer>
        <SubTitleText>
          <Trans
            t={t}
            i18nKey={'PartnerNewpluginsdkActivate.whenPromptedAlwaysAllow'}
          >
            <LinkText>AbsoluteText</LinkText>
          </Trans>
        </SubTitleText>
      </SubTitleTextContainer>
      <BodyContentContainer>
        <AlwayAllowImage src={images.partnerpluginsdk_always_allow} />
      </BodyContentContainer>
    </MultiStepActivationContainer>
  );
};

export default PartnerNewPluginActivateMultiStepAlwaysAllow;
